import * as Sentry from '@sentry/browser';

function shouldIgnoreError(error) {
  return isProbablyBlockedByAdblockNetworkError(error) || 
         isThirdPartyBrowserError(error);
}

function isProbablyBlockedByAdblockNetworkError(error) {
  const now = Date.now();

  if (!error.breadcrumbs) {
    return false;
  }

  // We go from the back since the last breadcrumb is most likely the erroneous one
  for (let i = error.breadcrumbs.length - 1; i >= 0; i--) {
    const breadcrumb = error.breadcrumbs[i];
    if (!breadcrumb) continue;

    // We only need to check the last 5s of breadcrumbs as any earlier breadcrumbs are definitely unrelated
    if (breadcrumb.timestamp && now - breadcrumb.timestamp * 1000 > 5000) {
      break;
    }

    if (isProbablyBlockedByAdblockBreadcrumb(breadcrumb)) {
      return true;
    }
  }

  return false;
}

function isProbablyBlockedByAdblockBreadcrumb(breadcrumb) {
  if (
    breadcrumb.level !== 'error' ||
    (breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch')
  ) {
    return false;
  }

  const url = breadcrumb.data?.url;
  if (!url) return false;

  return isProbablyBlockedByAdblockUrl(url);
}

function isProbablyBlockedByAdblockUrl(url) {
  const adblockPattern = /adblock/;
  const googleAnalyticsPattern = /https?:\/\/(www\.)?google-analytics\.com\/(collect(\?.*)?|analytics\.js|gtag\/js|gtm\.js(\?.*)?|fbevents\.js|ga4-[\w-]+\.js)/;
  const batPattern = /bat\.js/;

  return adblockPattern.test(url) || googleAnalyticsPattern.test(url) || batPattern.test(url);
}

function isThirdPartyBrowserError(error) {
  // Check for Facebook in-app browser errors
  if (isFacebookInAppBrowserError(error)) {
    return true;
  }

  // Check for other third-party browser/WebView errors
  if (isWebViewError(error)) {
    return true;
  }

  return false;
}

function isFacebookInAppBrowserError(error) {
  // Check error message for Facebook-specific issues
  const errorMessage = error.exception?.values?.[0]?.value || error.message || '';
  const facebookErrorPatterns = [
    /Java object is gone/,
    /FB_IAB/,
    /FBAV/,
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    /_AutofillCallbackHandler/,
  ];

  if (facebookErrorPatterns.some(pattern => pattern.test(errorMessage))) {
    return true;
  }

  // Check user agent for Facebook browser
  const userAgent = error.request?.headers?.['User-Agent'] || 
                   error.contexts?.browser?.name || 
                   navigator?.userAgent || '';
  
  if (/Facebook|FB_IAB|FBAV/i.test(userAgent)) {
    return true;
  }

  // Check referrer for Facebook
  const referrer = error.request?.headers?.Referer || 
                  error.contexts?.page?.referrer || 
                  document?.referrer || '';
  
  if (/facebook\.com|m\.facebook\.com/i.test(referrer)) {
    return true;
  }

  // Check URL parameters for Facebook traffic
  const url = error.contexts?.page?.url || window?.location?.href || '';
  if (/[?&](fbclid|utm_source=fb)/i.test(url)) {
    return true;
  }

  return false;
}

function isWebViewError(error) {
  const errorMessage = error.exception?.values?.[0]?.value || error.message || '';
  const webViewErrorPatterns = [
    /WebView/i,
    /Java object is gone/,
    /Non-Error promise rejection captured/,
    /Script error/,
    /Network request failed/,
    /Load failed/,
    /The operation couldn't be completed/,
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    /_AutofillCallbackHandler/,
  ];

  return webViewErrorPatterns.some(pattern => pattern.test(errorMessage));
}

Sentry.init({
  dsn: window.SENTRY_DSN_FRONTEND,
  environment: window.SENTRY_ENVIRONMENT,
  release: window.SENTRY_RELEASE,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  debug: window.SENTRY_ENVIRONMENT === 'development',
  beforeSend: (event) => {
    return shouldIgnoreError(event) ? null : event;
  },
  // This is a list of URLs that we want to allow to be sent to Sentry to prevent false positives
  allowUrls: [/https?:\/\/((prod-cdn|staging-cdn|www)\.)?laundryheap\.com/],
  ignoreErrors: [
    /Non-Error promise rejection/, 
    /Fetch globalNotice timeout/,
    // Facebook and third-party browser errors
    /Java object is gone/,
    /Script error/,
    /Network request failed/,
    /Load failed/,
    /The operation couldn't be completed/,
    /Non-Error promise rejection captured/,
    // WebView specific errors
    /WebView/i,
    // iOS WebKit message handler errors
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    // Autofill callback handler errors
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    /_AutofillCallbackHandler/,
    // Browser extension errors
    /Invalid call to runtime\.sendMessage/,
    /Tab not found/,
    // Third-party service errors
    /TrackerStorageType is not defined/,
    /globalThis.*is not defined/,
    // Sentry internal errors
    /Cannot set property message of.*which has only a getter/,
    // Browser security/storage errors
    /Failed to read the 'localStorage' property from 'Window': Access is denied/,
    /invalid origin/,
  ],
});

window.Sentry = Sentry;
